import { emptySplitApi } from "../api/apiSlice";
import {
  Users,
  UserState,
  DraftUser,
  Error,
  FormErrors,
  Message,
  DraftUserPayment,
  UserPayments,
  UserPayment,
  Salaries,
  Salary,
  Attendances,
  ClientState,
  DraftClient,
  WorkLogState,
  WorkLogs,
  DraftWorkLog,
  SalaryInfo,
  UserPaymentState,
  SalaryState,
  Departments,
  DraftDepartment,
  DepartmentState,
  Shifts,
  DraftShift,
  ShiftState,
} from "../api";

export const userApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<Users | Error, string | void>({
      query: (query) => ({
        url: `/users${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => {
        if (result?.users) {
          return [
            "User",
            ...result.users.map(({ id }) => ({ type: "User" as const, id })),
          ];
        } else {
          return ["User"];
        }
      },
    }),
    getUser: builder.query<UserState | Error, string | void>({
      query: (id) => ({
        url: `/users/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => [{ type: "User", id: arg }],
    }),
    getUserInDepth: builder.query<any | Error, string | void>({
      query: () => ({
        url: `/users/get-in-depth`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => [{ type: "User", id: arg }],
    }),
    exportUsers: builder.query<any | Error, string | void>({
      query: (query) => ({
        url: `/users/export${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    addNewUser: builder.mutation<UserState | FormErrors | Error, DraftUser>({
      query: (data) => ({
        url: "/users",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["User"],
    }),
    editUser: builder.mutation<UserState | FormErrors | Error, DraftUser>({
      query: (data) => ({
        url: "/users",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    destroyUser: builder.mutation<Message | FormErrors | Error, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    updateUserStatus: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/users/update-status",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Client", id: arg.id }],
    }),
    updateUserBalance: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/users/update-balance",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Client", id: arg.id }],
    }),
    getDepartments: builder.query<Departments | Error, string | void>({
      query: (query) => ({
        url: `/department${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => {
        if (result?.departments) {
          return [
            "Department",
            ...result.departments.map(({ id }) => ({
              type: "Department" as const,
              id,
            })),
          ];
        } else {
          return ["Department"];
        }
      },
    }),
    getDepartment: builder.query<DepartmentState | FormErrors | Error, string>({
      query: (id) => ({
        url: `/department/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => [{ type: "Department", id: arg }],
    }),
    addNewDepartment: builder.mutation<
      DepartmentState | FormErrors | Error,
      DraftDepartment
    >({
      query: (data) => ({
        url: "/department",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Department"],
    }),
    editDepartment: builder.mutation<
      DepartmentState | FormErrors | Error,
      DraftDepartment
    >({
      query: (data) => ({
        url: "/department",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Department", id: arg.id },
      ],
    }),
    destroyDepartment: builder.mutation<Message | FormErrors | Error, string>({
      query: (id) => ({
        url: `/department/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Department"],
    }),
    updateDepartmentStatus: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/department/update-status",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Department", id: arg.id },
      ],
    }),
    getShifts: builder.query<Shifts | Error, string | void>({
      query: (query) => ({
        url: `/shift${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => {
        if (result?.shifts) {
          return [
            "Shift",
            ...result.shifts.map(({ id }) => ({
              type: "Shift" as const,
              id,
            })),
          ];
        } else {
          return ["Shift"];
        }
      },
    }),
    getShift: builder.query<ShiftState | FormErrors | Error, string>({
      query: (id) => ({
        url: `/shift/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => [{ type: "Shift", id: arg }],
    }),
    addNewShift: builder.mutation<ShiftState | FormErrors | Error, DraftShift>({
      query: (data) => ({
        url: "/shift",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Shift"],
    }),
    editShift: builder.mutation<ShiftState | FormErrors | Error, DraftShift>({
      query: (data) => ({
        url: "/shift",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Shift", id: arg.id }],
    }),
    destroyShift: builder.mutation<Message | FormErrors | Error, string>({
      query: (id) => ({
        url: `/shift/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Shift"],
    }),
    updateShiftStatus: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/shift/update-status",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Shift", id: arg.id }],
    }),
    getWorkLogs: builder.query<WorkLogs | Error, string | void>({
      query: (query) => ({
        url: `/work-log${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => {
        if (result?.workLogs) {
          return [
            "WorkLog",
            ...result.workLogs.map(({ id }) => ({
              type: "WorkLog" as const,
              id,
            })),
          ];
        } else {
          return ["WorkLog"];
        }
      },
    }),
    getWorkLog: builder.query<WorkLogState | FormErrors | Error, string>({
      query: (id) => ({
        url: `/work-log/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => [{ type: "WorkLog", id: arg }],
    }),
    addNewWorkLog: builder.mutation<
      WorkLogState | FormErrors | Error,
      DraftWorkLog
    >({
      query: (data) => ({
        url: "/work-log",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // Production changes the stock and cost of products.
      invalidatesTags: ["WorkLog", "Product"],
    }),
    editWorkLog: builder.mutation<
      WorkLogState | FormErrors | Error,
      DraftWorkLog
    >({
      query: (data) => ({
        url: "/work-log",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "WorkLog", id: arg.id },
      ],
    }),
    destroyWorkLog: builder.mutation<Message | FormErrors | Error, string>({
      query: (id) => ({
        url: `/work-log/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // Deleting production gives back materials and takes out what was made.
      invalidatesTags: ["Product"],
    }),
    getPayments: builder.query<UserPayments | Error, string | void>({
      query: (query) => ({
        url: `/user-payment${query || "'"}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => {
      //   if (result?.payments) {
      //     return [
      //       "UserPayment",
      //       ...result.payments.map(({ id }) => ({
      //         type: "UserPayment" as const,
      //         id,
      //       })),
      //     ];
      //   } else {
      //     return ["UserPayment"];
      //   }
      // },
    }),
    getPayment: builder.query<UserPaymentState | any | Error, string | void>({
      query: (id) => ({
        url: `/user-payment/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => [{ type: "UserPayment", id: arg }],
    }),
    newPayment: builder.mutation<
      UserPayment | FormErrors | Error | any,
      DraftUserPayment
    >({
      query: (data) => ({
        url: "/user-payment",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["UserPayment"],
    }),
    editPayment: builder.mutation<
      UserPayment | FormErrors | Error | any,
      DraftUserPayment
    >({
      query: (data) => ({
        url: "/user-payment",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["UserPayment"],
    }),
    destroyPayment: builder.mutation<
      Message | FormErrors | Error | any,
      string
    >({
      query: (id) => ({
        url: `/user-payment/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    attendanceList: builder.query<Attendances | Error, string | void>({
      query: (query) => ({
        url: `/attendance${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => {
        if (result?.attendances) {
          return [
            "Attendance",
            ...result.attendances.map(({ id }) => ({
              type: "Attendance" as const,
              id,
            })),
          ];
        } else {
          return ["Attendance"];
        }
      },
    }),
    getAttendanceByUserIds: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance/attendance-by-user-ids",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    updateAttendance: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance",
        method: "PUT",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    destroyAttendance: builder.mutation<any | FormErrors | Error, string>({
      query: (id) => ({
        url: `/attendance/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    logTime: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["Attendance"],
    }),
    loginStatus: builder.query<Attendances | Error, string | void>({
      query: (query) => ({
        url: `/attendance/status${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => {
      //   if (result?.attendances) {
      //     return [
      //       "Attendance",
      //       ...result.attendances.map(({ id }) => ({
      //         type: "Attendance" as const,
      //         id,
      //       })),
      //     ];
      //   } else {
      //     return ["Attendance"];
      //   }
      // },
    }),
    logTimeBulk: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance/log-time-bulk",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["Attendance"],
    }),
    getDutyChartByUserIds: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance/dutychart-by-user-ids",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    addDutyChartBulk: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/attendance/add-dutychart-bulk",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["Attendance"],
    }),
    getSalaries: builder.query<Salaries | Error, string | void>({
      query: (query) => ({
        url: `/user-salary${query || ""}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => {
      //   if (result?.payments) {
      //     return [
      //       "UserPayment",
      //       ...result.payments.map(({ id }) => ({
      //         type: "UserPayment" as const,
      //         id,
      //       })),
      //     ];
      //   } else {
      //     return ["UserPayment"];
      //   }
      // },
    }),
    getSalary: builder.query<SalaryState | any | Error, string | void>({
      query: (id) => ({
        url: `/user-salary/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // providesTags: (result, error, arg) => [{ type: "UserPayment", id: arg }],
    }),
    getSalaryInfo: builder.mutation<SalaryInfo | FormErrors | Error | any, any>(
      {
        query: (data) => ({
          url: "/user-salary/info",
          method: "POST",
          body: data,
          validateStatus: (response, result) => {
            if (result?.invalidData || result?.error) {
              return true;
            }
            return response.ok;
          },
        }),
        invalidatesTags: ["SalaryInfo"],
      },
    ),
    approveSalary: builder.mutation<Salary | any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/user-salary/approve",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Salary"],
    }),
    addSalary: builder.mutation<Salary | any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/user-salary/add",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Salary"],
    }),
    updateMissingData: builder.mutation<FormErrors | Error | any, any>({
      query: (data) => ({
        url: "/user-salary/update-missing-data",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: [],
    }),
    calculateSalary: builder.mutation<Salary | FormErrors | Error, any>({
      query: (data) => ({
        url: "/user-salary",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: ["Salary"],
    }),
    destroySalary: builder.mutation<any | FormErrors | Error, any>({
      query: (id) => ({
        url: `/user-salary/${id}`,
        method: "DELETE",
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    getProfile: builder.query<UserState | FormErrors | Error, string>({
      query: (id) => ({
        url: `/profile/get/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => [{ type: "User", id: arg }],
    }),
    editProfile: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/profile",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    changePassword: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/profile/change-password",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    getClient: builder.query<ClientState | FormErrors | Error, string>({
      query: (id) => ({
        url: `/clients/${id}`,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      providesTags: (result, error, arg) => [{ type: "Client", id: arg }],
    }),
    updateClient: builder.mutation<
      ClientState | FormErrors | Error,
      DraftClient
    >({
      query: (data) => ({
        url: "/clients/update",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Client", id: arg.id }],
    }),
    updateClientSecureSettings: builder.mutation<any | FormErrors | Error, any>(
      {
        query: (data) => ({
          url: "/clients/update-secure-settings",
          method: "PATCH",
          body: data,
          validateStatus: (response, result) => {
            if (result?.invalidData || result?.error) {
              return true;
            }
            return response.ok;
          },
        }),
      },
    ),
    updatePrinterBaseUrl: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/clients/update-printer-base-url",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    updatePrinterUsedFor: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/clients/update-printer-used-for",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    updateBackupPrinter: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/clients/update-backup-printer",
        method: "PATCH",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    clientPayment: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["User"],
    }),
    clientAddOnPayment: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/addon",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["User"],
    }),
    clientVerifyPayment: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/verify",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["User"],
    }),
    clientPaymentBalance: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/balance",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["User"],
    }),
    clientVerifyPaymentBalance: builder.mutation<any | FormErrors | Error, any>(
      {
        query: (data) => ({
          url: "/client-payment/verify-balance",
          method: "POST",
          body: data,
          validateStatus: (response, result) => {
            if (result?.invalidData || result?.error) {
              return true;
            }
            return response.ok;
          },
        }),
        // invalidatesTags: ["User"],
      },
    ),
    // Prices for the pricing screen's selections, worked out by the API.
    getPaymentPricing: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/pricing",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    // The full price of one payment (plan, tax, wallet, processing fee).
    getPaymentQuote: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/quote",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    clientPaymentFailed: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/failed",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    getClientPaymentStatus: builder.query<any | Error, string>({
      query: (id) => ({
        url: `/client-payment/status/${id}`,
        validateStatus: (response, result) => {
          if (result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
    requestFreeAccess: builder.mutation<any | FormErrors | Error, any>({
      query: (data) => ({
        url: "/client-payment/request-free-access",
        method: "POST",
        body: data,
        validateStatus: (response, result) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
      // invalidatesTags: ["User"],
    }),
    verifyCode: builder.mutation({
      query: (data: any) => ({
        url: "/clients/verify-code",
        method: "POST",
        body: data,
        validateStatus: (response: any, result: any) => {
          if (result?.invalidData || result?.error) {
            return true;
          }
          return response.ok;
        },
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useGetUserInDepthQuery,
  useAddNewUserMutation,
  useEditUserMutation,
  useDestroyUserMutation,
  useUpdateUserStatusMutation,
  useUpdateUserBalanceMutation,
  useGetDepartmentsQuery,
  useLazyGetDepartmentsQuery,
  useLazyExportUsersQuery,
  useGetDepartmentQuery,
  useAddNewDepartmentMutation,
  useEditDepartmentMutation,
  useDestroyDepartmentMutation,
  useUpdateDepartmentStatusMutation,
  useGetShiftsQuery,
  useLazyGetShiftsQuery,
  useGetShiftQuery,
  useAddNewShiftMutation,
  useEditShiftMutation,
  useDestroyShiftMutation,
  useUpdateShiftStatusMutation,
  useGetWorkLogsQuery,
  useGetWorkLogQuery,
  useAddNewWorkLogMutation,
  useEditWorkLogMutation,
  useDestroyWorkLogMutation,
  useGetPaymentsQuery,
  useLazyGetPaymentQuery,
  useNewPaymentMutation,
  useEditPaymentMutation,
  useDestroyPaymentMutation,
  useAttendanceListQuery,
  useGetAttendanceByUserIdsMutation,
  useUpdateAttendanceMutation,
  useDestroyAttendanceMutation,
  useLogTimeMutation,
  useLoginStatusQuery,
  useLogTimeBulkMutation,
  useGetDutyChartByUserIdsMutation,
  useAddDutyChartBulkMutation,
  useGetSalariesQuery,
  useLazyGetSalaryQuery,
  useGetSalaryInfoMutation,
  useApproveSalaryMutation,
  useAddSalaryMutation,
  useUpdateMissingDataMutation,
  useCalculateSalaryMutation,
  useDestroySalaryMutation,
  useGetProfileQuery,
  useEditProfileMutation,
  useChangePasswordMutation,
  useGetClientQuery,
  useUpdateClientMutation,
  useUpdateClientSecureSettingsMutation,
  useUpdatePrinterBaseUrlMutation,
  useUpdatePrinterUsedForMutation,
  useUpdateBackupPrinterMutation,
  useClientPaymentMutation,
  useClientAddOnPaymentMutation,
  useClientVerifyPaymentMutation,
  useClientPaymentBalanceMutation,
  useClientVerifyPaymentBalanceMutation,
  useRequestFreeAccessMutation,
  useGetPaymentPricingMutation,
  useGetPaymentQuoteMutation,
  useClientPaymentFailedMutation,
  useLazyGetClientPaymentStatusQuery,
  useVerifyCodeMutation,
} = userApi;
